// @flow

import { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';

import type { AppState, ButtonTheme, NothingToDoScreenProps } from '../typings.js';
import { formatTime, isHalloween } from '../../utils/Utils';
import getNextShiftDescription from '../../timeClock/utils/getNextShiftDescription';
import breakIcon from '../../assets/svg/icons/break-white.svg';
import loginIcon from '../../assets/svg/icons/login-icon-white.svg';
import logoutIcon from '../../assets/svg/icons/logout-white.svg';
import ghostIcon from '../../assets/svg/Misc/ghost.svg';
import opacityRevealKeyframes from '../../keyframes/opacityRevealKeyframes';
import whenIsNextAvailableClockIn from '../../timeClock/utils/whenIsNextAvailableClockIn';
import {
  APP_STATE_CLOCKED_IN,
  APP_STATE_ON_BREAK,
  APP_STATE_END_BREAK,
  APP_STATE_CLOCKED_OUT,
  APP_STATE_NOTHING_TO_DO,
  DESCRIPTION_FIELD_MAP
} from '../../constants';
import floatKeyframes from '../../keyframes/floatKeyframes';

import RoundedCornerButton from './RoundedCornerButton';
import DisplayText from './Text/DisplayText';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  z-index: 5;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: transparent;

  opacity: 0;
  animation: ${opacityRevealKeyframes} 0.3s ease-in-out;
  animation-delay: 0.3s;
  animation-fill-mode: forwards;

  &.hide {
    display: none;
  }
`;

const GhostContainer = styled.div`
  animation: ${floatKeyframes} 3s ease-out infinite;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
`;

const Icon = styled.img`
  width: 64px;
  height: 64px;
  margin-bottom: 20px;
`;

const BackButtonContainer = styled.div`
  width: 256px;
  margin-top: 20px;
`;

type Description = {
  icon: string,
  buttonTheme: ButtonTheme,
  firstLine: string,
  secondLine: string
};

type Props = {
  appStatusVisible: boolean,
  appState: AppState,
  back: () => void,
  screenProps: NothingToDoScreenProps,
  usTimeFormat: boolean,
  clockInEarlyMinutes: number,
  deviceWidth: number,
  isIntegrationKiosk: boolean
};

type State = {
  className: string
};

export default class AppStatusScreen extends PureComponent<Props, State> {
  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ appStatusVisible }: Props) {
    this.setState({ className: appStatusVisible ? '' : 'hide' });
  }

  state = {
    className: 'hide'
  };

  renderNothingToDo(
    back: () => void,
    screenProps: NothingToDoScreenProps,
    clockInEarlyMinutes: number,
    usTimeFormat: boolean,
    isIntegrationKiosk: boolean
  ) {
    const { shift } = screenProps;
    const firstLine =
      getNextShiftDescription(shift, usTimeFormat) || "You don't have any shifts scheduled"; // check when next shift is and display if we have such info
    const secondLine = shift
      ? whenIsNextAvailableClockIn(shift, clockInEarlyMinutes)
      : 'Live your life to the fullest and come back later';

    return (
      <Container className={this.state.className}>
        <FormContainer>
          {!isHalloween() && <Icon src={logoutIcon} />}
          {isHalloween() && (
            <GhostContainer>
              <Icon src={ghostIcon} />
            </GhostContainer>
          )}
          <DisplayText fontSize="20px" bold textAlign="center">
            {firstLine}
          </DisplayText>
          <DisplayText fontSize="20px" bold textAlign="center" opacity="0.75" lineHeight="30px">
            {secondLine}
          </DisplayText>

          {!isIntegrationKiosk && (
            <BackButtonContainer>
              <RoundedCornerButton
                label="Back"
                buttonTheme={APP_STATE_CLOCKED_OUT}
                onPress={back}
                scale={1}
              />
            </BackButtonContainer>
          )}
        </FormContainer>
      </Container>
    );
  }

  isScreenAvailable(appState: AppState): boolean {
    return includes(
      [
        APP_STATE_CLOCKED_IN,
        APP_STATE_ON_BREAK,
        APP_STATE_END_BREAK,
        APP_STATE_CLOCKED_OUT,
        APP_STATE_NOTHING_TO_DO
      ],
      appState
    );
  }

  getDescription(appState: AppState, screenProps: {}, usTimeFormat: boolean): Description | void {
    const timeField = DESCRIPTION_FIELD_MAP[appState];
    const timeLabel = screenProps[timeField]
      ? ` at ${formatTime(screenProps[timeField], usTimeFormat)}`
      : '';
    const stateMap: {
      [AppState]: Description
    } = {
      [APP_STATE_CLOCKED_IN]: {
        icon: loginIcon,
        buttonTheme: APP_STATE_CLOCKED_IN,
        firstLine: `Clocked in${timeLabel}`,
        secondLine: 'Have a great day!'
      },
      [APP_STATE_ON_BREAK]: {
        icon: breakIcon,
        buttonTheme: APP_STATE_ON_BREAK,
        firstLine: `Break started${timeLabel}`,
        secondLine: 'See you soon!'
      },
      [APP_STATE_END_BREAK]: {
        icon: loginIcon,
        buttonTheme: APP_STATE_END_BREAK,
        firstLine: `Break ended${timeLabel}`,
        secondLine: 'Welcome back!'
      },
      [APP_STATE_CLOCKED_OUT]: {
        icon: logoutIcon,
        buttonTheme: APP_STATE_CLOCKED_OUT,
        firstLine: `Clocked out${timeLabel}`,
        secondLine: 'Until next time!'
      }
    };

    return stateMap[appState];
  }

  render() {
    const {
      appState,
      back,
      screenProps,
      usTimeFormat,
      clockInEarlyMinutes,
      isIntegrationKiosk
    } = this.props;
    if (!this.isScreenAvailable(appState)) {
      return null;
    }

    if (appState === APP_STATE_NOTHING_TO_DO) {
      return this.renderNothingToDo(
        back,
        screenProps,
        clockInEarlyMinutes,
        usTimeFormat,
        isIntegrationKiosk
      );
    }

    const description = this.getDescription(appState, screenProps, usTimeFormat);
    if (!description) {
      return null;
    }

    const { icon, buttonTheme, firstLine, secondLine } = description;
    return (
      <Container
        appState={appState}
        className={this.state.className}
        deviceWidth={this.props.deviceWidth}>
        <FormContainer deviceWidth={this.props.deviceWidth}>
          {!isHalloween() && <Icon src={icon} />}
          {isHalloween() && (
            <GhostContainer>
              <Icon src={ghostIcon} />
            </GhostContainer>
          )}
          <DisplayText fontSize="20px" bold textAlign="center">
            {firstLine}
          </DisplayText>
          <DisplayText fontSize="20px" bold textAlign="center" opacity="0.75" lineHeight="30px">
            {secondLine}
          </DisplayText>

          {!isIntegrationKiosk && (
            <BackButtonContainer>
              <RoundedCornerButton
                label="Back"
                buttonTheme={buttonTheme}
                onPress={back}
                scale={1}
              />
            </BackButtonContainer>
          )}
        </FormContainer>
      </Container>
    );
  }
}

AppStatusScreen.propTypes = {
  screenProps: PropTypes.object,
  appState: PropTypes.string.isRequired,
  back: PropTypes.func.isRequired,
  usTimeFormat: PropTypes.bool.isRequired,
  deviceWidth: PropTypes.number.isRequired,
  appStatusVisible: PropTypes.bool.isRequired,
  clockInEarlyMinutes: PropTypes.number.isRequired
};
